/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { CellType, CellRangeEventArgs, HeadersVisibility } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { SetupStep } from 'models/setup/SetupStep';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import { CrudCode } from 'models/common/Edit';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { findProjectStepType, saveProjectStepType } from 'apis/setup/SetupStep';
import { findSetupEquipment } from 'apis/setup/SetupEquipment';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { SuccessOrNot } from 'models/common/RestApi';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회 > 프로젝트 셋업단계 설정 > [단계 유형/공통단계] 탭 > 단계유형 그리드
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 * 2026.08.07   jshwang12       설비가 등록된 경우 단계 수정 불가 (가중치 포함)
 * 2026.08.25   jshwang12       프로젝트에 설정된 조직유형코드에 따라 설비군 조회, 단계 유형/공통단계 저장 수정 (개별 저장 -> 통합 저장)
 */

interface Props {
  isReadOnly?: boolean; // 읽기만 가능여부 (true인 경우 수정권한이 아예 없는 상태)
  orgTpCd?: string; // 조직유형코드
  setupStatus?: SetupStep[]; // 단계유형별 설정가능 상태정보
}

const StepTypeGrid = ({ isReadOnly = true, orgTpCd, setupStatus = [], ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [elmPrjId, setElmPrjId] = useState<string>();
  const [rowData, setRowData] = useState<SetupStep[]>([]);
  const [isOpenEquipmentGroupPopup, setOpenEquipmentGroupPopup] = useState<boolean>(false);
  const [equipmentGroupPopupCondition, setEquipmentGroupPopupCondition] = useState<any>();
  // 단계유형별 설비가 등록된 설비군ID 목록 (해당 설비군은 제거 불가)
  // cf. 서로 다른 단계유형에 동일한 설비군이 등록될 수 있으므로 단계유형 단위로 관리
  const [usedEqclIdsBySpTp, setUsedEqclIdsBySpTp] = useState<Record<string, string[]>>({});
  const dragIndex = useRef<any>();

  useImperativeHandle(ref, () => ({
    search: (params) => {
      setElmPrjId(params?.elmPrjId);
      handleSearch(params?.elmPrjId);
    },
    hasChanges: () => (rowData || []).findIndex((o) => !!o.crudKey) > -1,
    validate: () => {
      return validate();
    },
    save: () => {
      return new Promise((resolve) => {
        handleSave().then((res) => resolve(res));
      });
    },
    refresh: () => handleSearch(),
  }));

  // 프로젝트가 완료된 경우 단계유형 수정 불가 (행추가/순서변경/설비군/단계유형명/가중치)
  const readOnly = useMemo(() => {
    if (!(setupStatus || []).length) return true;
    return isReadOnly || setupStatus?.[0]?.prjCpltYn !== 'N';
  }, [isReadOnly, setupStatus]);

  // 단계유형별 설비여부 (설비가 등록된 단계유형은 삭제 불가)
  const mapEqpYn = useMemo(() => {
    return new Map((setupStatus || []).map((o) => [o.spTpCd, o.eqpYn]));
  }, [setupStatus]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = readOnly
        ? HeadersVisibility.Column
        : HeadersVisibility.All;
    }
  }, [readOnly]);

  const handleAddRow = () => {
    const newData = {
      crudKey: CrudCode.CREATE,
      istlDivsCd: '',
      prjSpCdDisplay: '',
      prjSpCd: '',
    };
    setRowData((prev) => [newData, ...prev]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    // 설비가 등록된 단계유형은 삭제 불가 (삭제 가능한 행만 처리하고 나머지는 안내)
    const blockedNms = [] as string[];
    const selectedIds = [] as number[];
    selectedRows.forEach((row) => {
      const item = row.dataItem;
      if (mapEqpYn.get(item?.spTpCd) === 'Y') {
        blockedNms.push(item?.spTpNm || item?.spTpCd);
        return;
      }
      selectedIds.push(parseInt(row.index!));
    });

    if (blockedNms.length > 0) {
      openMessageBar({
        type: 'warning',
        content: `${t(
          'setup.label.설비가 등록된 단계유형은 삭제할 수 없습니다.',
          '설비가 등록된 단계유형은 삭제할 수 없습니다.'
        )} (${blockedNms.join(', ')})`,
      });
    }
    if (selectedIds.length < 1) {
      return;
    }

    selectedIds.reverse().forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleSearch = (params = '') => {
    const id = params || elmPrjId;
    if (id) {
      findProjectStepType(id).then((result) => {
        setRowData(result);
      });
      // 설비가 등록된 설비군 목록 조회 (설비군 수정 시 제거 가능여부 판단)
      findSetupEquipment({ elmPrjId: id }).then((result) => {
        setUsedEqclIdsBySpTp(
          (result || []).reduce((acc, cur) => {
            const spTpCd = (cur.spTpCd || '').trim();
            const eqclId = (cur.eqclId || '').trim();
            if (spTpCd && eqclId && !(acc[spTpCd] || []).includes(eqclId)) {
              acc[spTpCd] = [...(acc[spTpCd] || []), eqclId];
            }
            return acc;
          }, {} as Record<string, string[]>)
        );
      });
    } else {
      setRowData([]);
      setUsedEqclIdsBySpTp({});
    }
  };

  const validate = () => {
    if (
      (rowData || []).length < 1 ||
      (rowData || []).filter((o) => o.crudKey !== CrudCode.DELETE).length < 1
    ) {
      return true;
    }

    // 단계유형명별 등록건수 (cf. {단계유형명: 건수, ..})
    const countSpTpNm = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .reduce((acc, cur) => {
        const spTpNm = (cur.spTpNm || '').trim();
        if (spTpNm) {
          acc[spTpNm] = (acc[spTpNm] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        if (_.isEmpty(o.eqclIds)) {
          return `${index + 1} : ${t(
            'setup.label.설비군을 선택해 주세요.',
            '설비군을 선택해 주세요.'
          )}`;
        }
        if (_.isEmpty(o.spTpNm)) {
          return `${index + 1} : ${t(
            'setup.label.단계 유형을 입력해 주세요.',
            '단계 유형을 입력해 주세요.'
          )}`;
        }
        // 단계유형명 중복 체크
        if (countSpTpNm[(o.spTpNm || '').trim()] > 1) {
          return `${index + 1} : ${t(
            'setup.label.중복된 단계 유형이 있습니다.',
            '중복된 단계 유형이 있습니다.'
          )} (${o.spTpNm})`;
        }
        if (_.isEmpty(o.wghtScor)) {
          return `${index + 1} : ${t(
            'setup.label.가중치를 입력해 주세요.',
            '가중치를 입력해 주세요.'
          )}`;
        }
        if (_.isNaN(Number(o.wghtScor))) {
          return `${index + 1} : ${t(
            'setup.label.가중치 값을 확인해 주세요.',
            '가중치 값을 확인해 주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    const totalWghtScor = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .reduce((acc, cur) => acc + Number(cur.wghtScor), 0);
    if (_.isNaN(totalWghtScor)) {
      openMessageBar({
        type: 'error',
        content: t('setup.label.가중치 값을 확인해 주세요.', '가중치 값을 확인해 주세요.'),
      });
      return false;
    }
    if (totalWghtScor !== 100) {
      openMessageBar({
        type: 'error',
        content: t(
          'setup.label.가중치 총 합을 100으로 맞춰 주세요.',
          '가중치 총 합을 100으로 맞춰 주세요.'
        ),
      });
      return false;
    }
    return true;
  };

  /**
   * 저장 (확인 모달/결과 처리는 상위 컴포넌트의 통합 저장에서 담당)
   */
  const handleSave = () => {
    return new Promise((resolve) => {
      if ((rowData || []).findIndex((o) => !!o.crudKey) < 0) {
        resolve({ successOrNot: SuccessOrNot.Y });
        return;
      }
      const params = (rowData || []).reduce(
        (acc, cur) => [...acc, { ...cur, elmPrjId: elmPrjId }],
        [] as SetupStep[]
      );
      saveProjectStepType(params)
        .then((res) => {
          resolve(res);
        })
        .catch(() => resolve({ successOrNot: SuccessOrNot.N }));
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.단계 유형 목록', '단계 유형 목록'),
      t('setup.label.단계 유형 목록', '단계 유형 목록')
    );
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if ('eqclIds' === binding) {
        cell.ariaReadOnly = readOnly;
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    if (readOnly) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;

    // 가중치
    if ('wghtScor' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onGridClick = useEvent((grid, e) => {
    const len = grid.rows.length;
    if (len == 0) return;

    const ht = grid.hitTest(e);
    if (ht.row < 0 || ht.col < 0) return;

    if (ht.panel === grid.cells) {
      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;
      // 설비군
      if ('eqclIds' === binding) {
        if (!readOnly) {
          const selectedIds = (item.eqclIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter((o) => !!o);
          setEquipmentGroupPopupCondition({
            selectedIds: (selectedIds || []).length ? selectedIds : undefined,
            target: {
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenEquipmentGroupPopup(true);
        }
      }
    }
  });

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.headersVisibility = readOnly ? HeadersVisibility.Column : HeadersVisibility.All;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => onGridClick(grid, e));

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
  });

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: readOnly,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: !readOnly,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서', '순서')),
        minWidth: 43,
        width: 43,
        visible: !readOnly,
        isReadOnly: true,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
      },
      {
        binding: 'eqclIds',
        header: String(t('setup.label.설비군', '설비군')),
        minWidth: 80,
        width: 200,
        isRequired: false,
        isReadOnly: true,
        align: 'left',
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
          <span>${params.value || ''}</span>
          <Button />  
        `,
      },
      {
        binding: 'spTpNm',
        header: String(t('setup.label.단계 유형', '단계 유형')),
        align: 'left',
        minWidth: 40,
        width: '*',
      },
      {
        binding: 'wghtScor',
        header: String(t('setup.label.가중치', '가중치')),
        align: 'center',
        minWidth: 70,
        width: 100,
      },
    ];
  }, [readOnly]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.단계 유형', '단계 유형')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {!readOnly && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={() => handleSearch()}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={518}
        allowPinning={false}
        allowSorting={false}
        excludeFilter={['sortable']}
        isReadOnly={readOnly}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
        initialized={onInitialized}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            if (item.crudKey !== CrudCode.CREATE) {
              item.crudKey = CrudCode.UPDATE;
            }
          });
        }}
      />
      {isOpenEquipmentGroupPopup && !readOnly && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopup}
          close={() => setOpenEquipmentGroupPopup(false)}
          type={'setup'}
          condition={{
            equipmentGroup: '',
            orgTpCd: orgTpCd || '',
          }}
          selectedIds={equipmentGroupPopupCondition?.selectedIds}
          isOnlyUsed={true}
          singleSelect={false}
          onCallback={(value) => {
            const target = equipmentGroupPopupCondition?.target;
            if (typeof target?.row === 'number') {
              const result = (value as EquipmentGroupCondition[]) || [];
              const item = gridRef.current?.rows[target?.row].dataItem;
              const prevEqclIds = item.eqclIds || '';
              const selectedIds = result
                .map((o) => o.equipmentGroup)
                .filter((o) => !!o) as string[];

              // 설비가 등록된 설비군은 제거할 수 없으므로 유지 (제거 가능한 항목만 반영하고 나머지는 안내)
              // cf. 동일한 설비군이 다른 단계유형에 등록될 수 있어 해당 행의 단계유형 기준으로 판단
              const usedEqclIds = usedEqclIdsBySpTp[(item?.spTpCd || '').trim()] || [];
              const prevIds = prevEqclIds
                .split(',')
                .map((id) => id.trim())
                .filter((o) => !!o);
              const blockedIds = prevIds.filter(
                (id) => usedEqclIds.includes(id) && !selectedIds.includes(id)
              );
              if (blockedIds.length > 0) {
                openMessageBar({
                  type: 'warning',
                  content: `${t(
                    'setup.label.설비가 등록된 설비군은 제거할 수 없습니다.',
                    '설비가 등록된 설비군은 제거할 수 없습니다.'
                  )} (${blockedIds.join(', ')})`,
                });
              }
              const finalIds = [...selectedIds];
              blockedIds.forEach((id) => {
                if (!finalIds.includes(id)) {
                  finalIds.push(id);
                }
              });

              const eqclIds = finalIds.join(', ');
              gridRef.current?.setCellData(target?.row, target?.col, eqclIds);
              const isUpdate = eqclIds !== prevEqclIds || !item.spTpNm;
              if (!item.spTpNm) {
                item.spTpNm = finalIds?.[0];
              }
              if (isUpdate && item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
                gridRef.current?.refresh();
              }
            }
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(StepTypeGrid);
